import { Box, Text } from '@chakra-ui/react'
import { useState, useEffect } from 'react'

type TeamColor = 'red' | 'blue' | 'purple' | 'yellow' | 'orange' | 'green'

interface TennisScoreBoxProps {
  score: number | string
  name?: string
  color: string
  dark?: boolean
  isTiebreak?: boolean
  onClick?: () => void
  teamColor?: TeamColor
  isController?: boolean
  team1Name?: string
  team2Name?: string
  isTeam1?: boolean
  lastScoringTeam?: 'team1' | 'team2' | 'none' | null
}

export function TennisScoreBox({ score, name, color, dark = false, isTiebreak = false, onClick, teamColor, isController = false, team1Name, team2Name, isTeam1, lastScoringTeam }: TennisScoreBoxProps) {
  const [isAnimating, setIsAnimating] = useState(false)

  // Trigger animation when this team scores (but not on initial render)
  useEffect(() => {
    if (!dark && lastScoringTeam && 
        ((isTeam1 && lastScoringTeam === 'team1') || (!isTeam1 && lastScoringTeam === 'team2'))) {
      setIsAnimating(true)
      const timer = setTimeout(() => {
        setIsAnimating(false)
      }, 2500)
      return () => clearTimeout(timer)
    }
  }, [lastScoringTeam, dark, isTeam1])

  const bgColor = dark ? '#101010' : (isAnimating ? 'black' : color)
  const textColor = dark ? color : (color === '#e2e700' || color === '#f73e01' ? 'black' : 'white')
  const displayTextColor = dark ? color : (isAnimating ? (teamColor === 'yellow' || teamColor === 'orange' ? color : 'white') : (color === '#e2e700' || color === '#f73e01' ? 'black' : 'white'))

  const phonetic = teamColor === 'red' ? 'Romeo' :
                   teamColor === 'blue' ? 'Bravo' :
                   teamColor === 'green' ? 'Golf' :
                   teamColor === 'yellow' ? 'Yankee' :
                   teamColor === 'orange' ? 'Oscar' : 'Charlie'

  // Determine display name
  const getDisplayName = () => {
    if (name && name.trim() !== '') {
      return name
    }
    
    // If this team has no name, check if the other team has a name
    const otherTeamName = isTeam1 ? team2Name : team1Name
    if (otherTeamName && otherTeamName.trim() !== '') {
      return 'Opponent'
    }
    
    return null // Show nothing if both teams have no names
  }

  const displayName = getDisplayName()

  return (
    <Box
      w="45%"
      p={4}
      borderRadius="lg"
      bg={bgColor}
      textAlign="center"
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      border={`2px solid ${color}`}
      flexShrink={0}
    >
      {displayName && (
        <Text fontSize="xl" fontWeight="bold" mb={2} color={displayTextColor}>
          {displayName}
        </Text>
      )}
      <Text
        fontSize={["80px", "100px", "120px", "140px", "160px"]}
        fontWeight="bold"
        color={displayTextColor}
        animation="scaleEffect 2.5s ease-out"
        sx={{
          '@keyframes scaleEffect': {
            '0%': { transform: 'scale(1.6)', opacity: 0 },
            '50%': { transform: 'scale(1.6)', opacity: 1 },
            '100%': { transform: 'scale(1)', opacity: 1 }
          }
        }}
      >
        {score}
      </Text>
      <Box mt={2}>
        {isController ? (
          <Box
            display="flex"
            flexDirection={{ base: "column", sm: "row" }}
            alignItems="center"
            justifyContent="center"
            gap={{ base: 0, sm: 1 }}
            flexWrap="wrap"
          >
            <Text fontSize="sm" color={displayTextColor}>
              Say "{phonetic}" or "{teamColor ? teamColor.charAt(0).toUpperCase() + teamColor.slice(1) : ''}"
            </Text>
          </Box>
        ) : (
          <Text fontSize="xs" color={displayTextColor}>
            Display Only
          </Text>
        )}
      </Box>
    </Box>
  )
} 